import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BosDurum, Buton, IskeletKart, Kart } from "@koclukpanel/ui";
import { AdresFormu } from "./AdresFormu";
import { ApiHatasi } from "./api";
import { veliSiparisOnayla, veliSiparisOnaylari, veliSiparisReddet } from "./api-magaza";
import type { SiparisAdresi, VeliSiparisOnayi as Onay } from "./magaza-tipleri";

/**
 * VELİ SİPARİŞ ONAYI — F-GAM-17, oyunlaştırma dalga 2 / sürüm 7a
 *
 * ================================================================
 * VELİ BURADA PUAN, BAKİYE YA DA ROZET GÖRMEZ (Emre S12)
 * ================================================================
 * Veliden istenen şey bir ÜRÜNÜ onaylayıp adresi vermek; çocuğun puan durumu bu
 * kararın parçası değil ve veliye gösterilmesi çocuğun çabasının ölçüsünü ailede
 * bir baskı aracına çevirir. Sunucu zaten bu alanları göndermiyor
 * (`VeliSiparisOnayi` tipi: yalnız ad, ürün, görsel, son gün); ekran da yazmıyor.
 * Reddetme metni de bu yüzden "puan iadesi"nden söz etmiyor.
 *
 * ================================================================
 * SON GÜN GÖRÜNÜR: 7 GÜN, SONRA OTOMATİK İPTAL (Emre S3)
 * ================================================================
 * Veli portala haftada bir bakıyor. Tarihi ve "kaç gün kaldı"yı kartta yazmak,
 * sessiz bir iptalin velinin haberi olmadan gelmesini önler. Kalan gün yalnız
 * GÖSTERİM içindir; sürenin dolduğuna ve iptale SUNUCU karar verir.
 *
 * Onay ve adres TEK istektir (`veliSiparisOnayla`): yarım kalan bir "onayladım ama
 * adres gitmedi" durumu yok. Ret de tek istektir. Kapsam ve IDOR sunucuda.
 * 18 altı, portal velisi olmayan sipariş bu ekranın konusu değil (S11: yönetici).
 */

const ISTANBUL = "Europe/Istanbul";
const GUN_MS = 86_400_000;

/** İstanbul takvim gününü ve "gg.aa.yyyy" metnini verir; tarih geçersizse `null`. */
function istanbulGunu(tarih: Date): { gunNo: number; metin: string } | null {
  if (Number.isNaN(tarih.getTime())) return null;
  const parcalar = new Intl.DateTimeFormat("en-GB", {
    timeZone: ISTANBUL,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).formatToParts(tarih);
  const al = (tur: string) => Number(parcalar.find((p) => p.type === tur)?.value);
  const gun = al("day");
  const ay = al("month");
  const yil = al("year");
  return {
    gunNo: Math.round(Date.UTC(yil, ay - 1, gun) / GUN_MS),
    metin: `${String(gun).padStart(2, "0")}.${String(ay).padStart(2, "0")}.${yil}`,
  };
}

/**
 * Son gün satırı: "08.10.2026 tarihine kadar" + kalan gün. Gün farkı İSTANBUL
 * takvim gününe göre (gece yarısı sınıfı: UTC'ye göre alınsa 21:00-24:00 arası
 * bir gün yanlış görünürdü). Geçersiz tarihte `null`: yanlış bir tarih yazmak,
 * hiç yazmamaktan kötüdür.
 */
export function sonGunMetni(sonGun: string, simdi: Date = new Date()): string | null {
  const son = istanbulGunu(new Date(sonGun));
  const bugun = istanbulGunu(simdi);
  if (!son || !bugun) return null;
  const kalan = son.gunNo - bugun.gunNo;
  const kalanMetni =
    kalan < 0
      ? "süre doldu"
      : kalan === 0
        ? "son gün bugün"
        : kalan === 1
          ? "1 gün kaldı"
          : `${kalan} gün kaldı`;
  return `${son.metin} tarihine kadar · ${kalanMetni}`;
}

type Asama = "adres" | "reddet";

const GENEL_HATA = "İşlem tamamlanamadı. Bağlantınızı kontrol edip tekrar deneyin.";

export function VeliSiparisOnayi() {
  const istemci = useQueryClient();
  // Aynı anda tek kart açık: iki açık form iki ayrı adres ve karışık hata yeri demek.
  const [secim, setSecim] = useState<{ id: string; asama: Asama } | null>(null);
  const [panelHatasi, setPanelHatasi] = useState<string | null>(null);
  const [sayfaHatasi, setSayfaHatasi] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<string | null>(null);

  const liste = useQuery<Onay[]>({
    queryKey: ["veli-siparis-onaylari"],
    queryFn: veliSiparisOnaylari,
    retry: false,
  });

  function yenile() {
    return istemci.invalidateQueries({ queryKey: ["veli-siparis-onaylari"] });
  }

  function hatayiIsle(err: unknown) {
    const mesaj = err instanceof ApiHatasi ? err.message : GENEL_HATA;
    // 404/409: sipariş artık yok ya da durumu değişti (süre doldu, başka yerden işlendi).
    // Kart listeden düşecek; hata kartla birlikte kaybolmasın diye sayfa düzeyinde kalır.
    if (err instanceof ApiHatasi && (err.durum === 404 || err.durum === 409)) {
      setSecim(null);
      setPanelHatasi(null);
      setSayfaHatasi(mesaj);
      void yenile();
      return;
    }
    setPanelHatasi(mesaj);
  }

  const onayla = useMutation({
    mutationFn: (v: { id: string; adres: SiparisAdresi }) => veliSiparisOnayla(v.id, v.adres),
    onSuccess: () => {
      setSecim(null);
      setPanelHatasi(null);
      setSonuc("Sipariş onaylandı. Hazırlanmaya başlanacak.");
      void yenile();
    },
    onError: hatayiIsle,
  });

  const reddet = useMutation({
    mutationFn: (id: string) => veliSiparisReddet(id),
    onSuccess: () => {
      setSecim(null);
      setPanelHatasi(null);
      setSonuc("Sipariş reddedildi ve iptal edildi.");
      void yenile();
    },
    onError: hatayiIsle,
  });

  function ac(id: string, asama: Asama) {
    onayla.reset();
    reddet.reset();
    setPanelHatasi(null);
    setSayfaHatasi(null);
    setSonuc(null);
    setSecim({ id, asama });
  }

  function kapat() {
    setSecim(null);
    setPanelHatasi(null);
  }

  if (liste.isPending) return <IskeletKart adet={2} />;

  if (liste.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Sipariş onayları alınamadı"
        aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const onaylar = liste.data;
  const yukleniyor = onayla.isPending || reddet.isPending;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-notr-900 text-lg font-semibold">Sipariş onayı</h2>

      {sonuc && (
        <p role="status" className="rounded-alan bg-onay-50 text-onay-700 px-3 py-2 text-sm">
          {sonuc}
        </p>
      )}
      {sayfaHatasi && (
        <p role="alert" className="rounded-alan bg-hata-50 text-hata-700 px-3 py-2 text-sm">
          {sayfaHatasi}
        </p>
      )}

      {onaylar.length === 0 ? (
        <p className="text-notr-600 text-sm">Onay bekleyen sipariş yok.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {onaylar.map((o) => {
            const acik = secim?.id === o.id ? secim.asama : null;
            return (
              <OnayKarti
                key={o.id}
                onay={o}
                acik={acik}
                yukleniyor={yukleniyor}
                panelHatasi={acik ? panelHatasi : null}
                onAc={(asama) => ac(o.id, asama)}
                onKapat={kapat}
                onAdresGonder={(adres) => onayla.mutate({ id: o.id, adres })}
                onReddet={() => reddet.mutate(o.id)}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

function OnayKarti({
  onay,
  acik,
  yukleniyor,
  panelHatasi,
  onAc,
  onKapat,
  onAdresGonder,
  onReddet,
}: {
  onay: Onay;
  acik: Asama | null;
  yukleniyor: boolean;
  panelHatasi: string | null;
  onAc: (asama: Asama) => void;
  onKapat: () => void;
  onAdresGonder: (adres: SiparisAdresi) => void;
  onReddet: () => void;
}) {
  const sonGun = sonGunMetni(onay.sonGun);
  const panelRef = useRef<HTMLDivElement>(null);
  const baslikKimligi = `siparis-${onay.id}-baslik`;

  // Panel açılınca odak içine taşınır: klavye ve ekran okuyucu kullanıcısı, düğmeye
  // bastığı yerde kalıp açılan forma ulaşamamasın.
  useEffect(() => {
    if (acik) panelRef.current?.focus();
  }, [acik]);

  return (
    <Kart olarak="li">
      <div className="flex gap-3">
        <div className="min-w-0">
          <p className="text-notr-600 text-xs">{onay.cocukAdi} için sipariş</p>
          <h3 className="text-notr-900 text-base font-semibold break-words">{onay.urunAdi}</h3>
          {sonGun && <p className="text-notr-800 mt-1 text-sm font-medium">{sonGun}</p>}
        </div>
      </div>
      <p className="text-notr-600 mt-2 text-xs">
        Bu tarihe kadar onaylanmazsa sipariş iptal edilir.
      </p>

      {acik === null && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Buton
            onClick={() => onAc("adres")}
            disabled={yukleniyor}
            aria-label={`Onayla ve adresi gir: ${onay.urunAdi}`}
          >
            Onayla ve adresi gir
          </Buton>
          <Buton
            gorunum="tehlike"
            onClick={() => onAc("reddet")}
            disabled={yukleniyor}
            aria-label={`Reddet: ${onay.urunAdi}`}
          >
            Reddet
          </Buton>
        </div>
      )}

      {acik === "adres" && (
        <div
          ref={panelRef}
          tabIndex={-1}
          role="group"
          aria-labelledby={baslikKimligi}
          className="border-notr-200 mt-3 border-t pt-3 focus:outline-none"
        >
          <h4 id={baslikKimligi} className="text-notr-900 text-sm font-semibold">
            Teslimat adresi
          </h4>
          <p className="text-notr-600 mt-0.5 mb-3 text-xs">
            Adresi gönderdiğinizde sipariş onaylanır ve hazırlanmaya başlanır.
          </p>
          <AdresFormu
            gonderEtiketi="Siparişi onayla"
            onGonder={onAdresGonder}
            yukleniyor={yukleniyor}
            hata={panelHatasi}
            onVazgec={onKapat}
          />
        </div>
      )}

      {acik === "reddet" && (
        <div
          ref={panelRef}
          tabIndex={-1}
          role="group"
          aria-labelledby={baslikKimligi}
          className="border-notr-200 mt-3 border-t pt-3 focus:outline-none"
        >
          <h4 id={baslikKimligi} className="text-notr-900 text-sm font-semibold">
            Siparişi reddetmek istiyor musunuz?
          </h4>
          <p className="text-notr-600 mt-0.5 text-sm">Sipariş iptal edilir.</p>
          {panelHatasi && (
            <p
              role="alert"
              className="rounded-alan bg-hata-50 text-hata-700 mt-2 px-3 py-2 text-sm"
            >
              {panelHatasi}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Buton gorunum="tehlike" onClick={onReddet} yukleniyor={yukleniyor}>
              Evet, reddet
            </Buton>
            <Buton gorunum="sessiz" onClick={onKapat} disabled={yukleniyor}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}
    </Kart>
  );
}
